import tenantConstants from '@constants';
import React, { useEffect, useMemo, useState } from 'react';
import { Button, ErrorMessage, Group, TextInput } from '../../common';

import { useTranslation } from 'react-i18next';
import Label from '../../common/Label/Label';
import { IconStyled } from '../../common/icon/IconStyled';
import Icon from '../../common/icon/icon';
import { FieldIconBox } from '../../common/image-uploads/styled';
import MediaGalleryModal from '../media-gallery/media-gallery-modal';
import {
  AddVideo,
  DeleteVideoBtn,
  VideoCard,
  VideoDesc,
  VideoInputBox,
  VideoInputLine,
  VideoPreview,
  VideoTitle,
} from './styled';

const REDESIGN_FORM = !!tenantConstants?.FORM;
const MAX_VIDEOS = 4;

const YOUTUBE_ID_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/;

const getYoutubeId = (url) => {
  if (!url) return null;
  const match = String(url).match(YOUTUBE_ID_RE);
  return match ? match[1] : null;
};

/* Compact YouTube glyph (matches Figma youtube.svg — red badge + white play triangle). */
const YoutubeGlyph = () => (
  <svg width="17" height="12" viewBox="0 0 24 17" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path
      d="M23.5 2.64a3.01 3.01 0 0 0-2.12-2.13C19.5 0 12 0 12 0S4.5 0 2.62.51A3.01 3.01 0 0 0 .5 2.64 31.3 31.3 0 0 0 0 8.5a31.3 31.3 0 0 0 .5 5.86 3.01 3.01 0 0 0 2.12 2.13C4.5 17 12 17 12 17s7.5 0 9.38-.51a3.01 3.01 0 0 0 2.12-2.13A31.3 31.3 0 0 0 24 8.5a31.3 31.3 0 0 0-.5-5.86Z"
      fill="#FF0000"
    />
    <path d="M9.55 12.14 15.82 8.5 9.55 4.86v7.28Z" fill="#fff" />
  </svg>
);

/**
 * One YouTube link row (Bayut redesign): teal card with [youtube glyph | url input | delete],
 * and — once a valid link is present — a preview card (thumbnail + title + channel).
 * Title/channel are fetched best-effort from YouTube oEmbed; falls back to stored values.
 */
const VideoLinkRow = ({ item, index, value, valueKey, setFieldValue, errorMsg, onPreview, t }) => {
  const url = item?.url || item?.link || '';
  const videoId = getYoutubeId(url);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!videoId) {
      setMeta(null);
      return undefined;
    }
    let active = true;
    fetch(`https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data) setMeta({ title: data.title, author: data.author_name });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [videoId]);

  const title = meta?.title || item?.title || '';
  const channel = meta?.author || item?.channel || item?.author || '';
  const thumb = videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;

  return (
    <VideoCard>
      <VideoInputLine>
        <YoutubeGlyph />
        <VideoInputBox>
          <input
            value={url}
            placeholder={t('Paste youtube video link here')}
            onChange={(e) => {
              const next = [...value];
              const newUrl = e.target.value;
              next[index] = { ...item, url: newUrl, ...(newUrl === '' && { link: '' }) };
              setFieldValue(valueKey, next, true);
            }}
          />
        </VideoInputBox>
        <DeleteVideoBtn
          $filled
          type="button"
          aria-label={t('Delete Video')}
          onClick={() => {
            const next = [...value];
            next[index] = { ...next[index], _destroy: true };
            setFieldValue(valueKey, next, true);
          }}
        >
          <Icon icon="RxCross2" size={14} color="#F73131" />
        </DeleteVideoBtn>
      </VideoInputLine>

      {!!videoId && (
        // Click the video preview to open the same gallery as the listing preview (Videos tab).
        <VideoPreview onClick={onPreview} style={onPreview ? { cursor: 'pointer' } : undefined}>
          {thumb && <img src={thumb} alt="" onError={(e) => (e.currentTarget.style.visibility = 'hidden')} />}
          <div className="meta">
            <p className="title">{title || url}</p>
            {channel && <p className="channel">{channel}</p>}
          </div>
        </VideoPreview>
      )}

      {!!errorMsg && <ErrorMessage message={errorMsg} />}
    </VideoCard>
  );
};

/* Trailing "Paste youtube video link here" row — keeps its own draft so typing never
   loses focus; commits a new entry on a complete YouTube link or on blur. */
const AddVideoRow = ({ value, valueKey, setFieldValue, hostList, t }) => {
  const [draft, setDraft] = useState('');

  const commit = (val) => {
    const next = (val || '').trim();
    if (!next) return;
    setFieldValue(valueKey, [...(value || []), { host: hostList?.[0]?.value, url: next, title: '' }], true);
    setDraft('');
  };

  return (
    <VideoCard>
      <VideoInputLine>
        <YoutubeGlyph />
        <VideoInputBox>
          <input
            value={draft}
            placeholder={t('Paste youtube video link here')}
            onChange={(e) => {
              const val = e.target.value;
              setDraft(val);
              if (getYoutubeId(val)) commit(val);
            }}
            onBlur={() => commit(draft)}
          />
        </VideoInputBox>
        <DeleteVideoBtn $filled={false} type="button" disabled aria-hidden="true" tabIndex={-1}>
          <Icon icon="RxCross2" size={14} color="#707070" />
        </DeleteVideoBtn>
      </VideoInputLine>
    </VideoCard>
  );
};

const VideoSelect = (props) => {
  const { label, labelIcon, value, allValues, setFieldValue, style, errorMsg, valueKey, hostList, isMobile } = props;
  const { t } = useTranslation();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryInitialVidIndex, setGalleryInitialVidIndex] = useState(0);

  const videos = useMemo(() => (value ? value.filter((e) => !e._destroy) : []), [value]);
  const usableVids = useMemo(
    () => (value || []).filter((v) => !v?._destroy && getYoutubeId(v?.url || v?.link)),
    [value],
  );

  // Bayut redesign — full Figma layout (gray-box field icon, teal link rows, preview cards).
  if (REDESIGN_FORM) {
    const canAddMore = videos.length < MAX_VIDEOS;
    return (
      <Group gap="16px" template="max-content minmax(0, 1fr)" style={{ ...style }}>
        <FieldIconBox>
          <Icon icon={labelIcon || 'AiOutlineVideoCamera'} color="#707070" size={17} />
        </FieldIconBox>
        <Group gap="12px" template="minmax(0, 1fr)">
          <div name={valueKey} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <VideoTitle>{label}</VideoTitle>
            <VideoDesc>{t('Bring your property to life. Add a youtube tour link below.')}</VideoDesc>
          </div>

          {(value || []).map((item, index) => {
            if (item._destroy) return null;
            const vidIdx = usableVids.findIndex((v) => v === item);
            return (
              <VideoLinkRow
                key={index}
                item={item}
                index={index}
                value={value}
                valueKey={valueKey}
                setFieldValue={setFieldValue}
                errorMsg={errorMsg?.[index]}
                onPreview={() => {
                  setGalleryInitialVidIndex(vidIdx >= 0 ? vidIdx : 0);
                  setGalleryOpen(true);
                }}
                t={t}
              />
            );
          })}

          {/* Trailing empty row to add another link (matches Figma "Paste youtube video link here"). */}
          {canAddMore && (
            <AddVideoRow value={value} valueKey={valueKey} setFieldValue={setFieldValue} hostList={hostList} t={t} />
          )}
        </Group>

        <MediaGalleryModal
          visible={galleryOpen}
          initialTab="videos"
          initialIndex={galleryInitialVidIndex}
          onClose={() => setGalleryOpen(false)}
          images={allValues?.property_images || allValues?.images || []}
          videos={value || []}
        />
      </Group>
    );
  }

  return (
    <Group gap="16px" template="max-content auto">
      {labelIcon && (
        <IconStyled>
          <Icon icon={labelIcon} />
        </IconStyled>
      )}
      <Group gap="8px" template="initial">
        <div name={valueKey}>
          <Label>{label}</Label>
          <div className="text-muted">
            {t('Add videos of your property from Youtube. Upload on Youtube and paste the link below.')}
          </div>
        </div>
        {!!videos.length && (
          <AddVideo gap="16px" template={'auto'}>
            {value.map((item, index) => {
              return (
                !item._destroy && (
                  <div style={{ position: 'relative' }} key={index}>
                    <TextInput
                      prefixIcon={
                        <Icon
                          icon={hostList.find((e) => e.value == item.host)?.icon || 'AiFillYoutube'}
                          color={hostList.find((e) => e.value == item.host)?.color || '#FF0000'}
                          size="1.2em"
                        />
                      }
                      label={t('Video Link')}
                      muted
                      placeholder={t('Place video link here')}
                      value={item.url || item.link || ''}
                      onChange={(e) => {
                        const fieldValue = [...value];
                        fieldValue[index] = { ...item, url: e.target.value, ...(e.target.value === '' && { link: '' }) };
                        setFieldValue(valueKey, fieldValue, true);
                      }}
                    />
                    <Button
                      type="link"
                      className="btn-close"
                      icon="HiOutlineTrash"
                      size="small"
                      style={{ '--btn-content-color': '#f5222d' }}
                      onClick={() => {
                        const fieldValue = [...value];
                        fieldValue[index] = { ...fieldValue[index], _destroy: true };
                        setFieldValue(valueKey, fieldValue, true);
                      }}
                    >
                      {t('Delete Video')}
                    </Button>
                    {!!errorMsg?.[index] && <ErrorMessage message={errorMsg?.[index]} />}
                  </div>
                )
              );
            })}
          </AddVideo>
        )}
        {(!value || videos?.length <= 3) && (
          <div>
            <Button
              type="primary"
              outlined
              onClick={() => {
                setFieldValue(valueKey, [...(value || []), { host: hostList[0].value, url: '', title: '' }]);
              }}
              style={{ fontSize: 14 }}
            >
              {videos?.length > 0 ? t('Add Another Video') : t('Add Video')}
            </Button>
          </div>
        )}
      </Group>
    </Group>
  );
};

export default VideoSelect;
